import { fromEnumAsString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/dto.js';
import { EventBookingProtocol } from './event-booking-protocol.js';
import { EventBookingStrategy } from './event-booking-strategy.js';

export class TicketsOnlyEventBookingProtocol extends EventBookingProtocol {
  static readonly TYPE_ID: string = 'e0af97a8699d4d4fa47ccd43d05943fe';

  public readonly strategy: EventBookingStrategy = EventBookingStrategy.Tickets;

  private constructor(
    public readonly sendOnlyPaymentConfirmation: boolean,
    public readonly allowExtendedCartSize: boolean,
    public readonly deferOrderCompletion: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return TicketsOnlyEventBookingProtocol.TYPE_ID;
  }

  static create(args?: {
    sendOnlyPaymentConfirmation?: boolean,
    allowExtendedCartSize?: boolean,
    deferOrderCompletion?: boolean,
  }): TicketsOnlyEventBookingProtocol {
    return new TicketsOnlyEventBookingProtocol(
        args?.sendOnlyPaymentConfirmation ?? false,
        args?.allowExtendedCartSize ?? false,
        args?.deferOrderCompletion ?? false,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketsOnlyEventBookingProtocol {
    return new TicketsOnlyEventBookingProtocol(
        MapUtils.get<boolean>(map, 'sendOnlyPaymentConfirmation'),
        MapUtils.get<boolean>(map, 'allowExtendedCartSize'),
        MapUtils.get<boolean>(map, 'deferOrderCompletion'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'strategy': fromEnumAsString(this.strategy),
      'sendOnlyPaymentConfirmation': this.sendOnlyPaymentConfirmation,
      'allowExtendedCartSize': this.allowExtendedCartSize,
      'deferOrderCompletion': this.deferOrderCompletion,
    };
  }

  clone(): this {
    return new TicketsOnlyEventBookingProtocol(
        this.sendOnlyPaymentConfirmation,
        this.allowExtendedCartSize,
        this.deferOrderCompletion,
    ) as this;
  }

  isSame(other: TicketsOnlyEventBookingProtocol): boolean {
    if(this === other) {
      return true;
    }

    return other.strategy === this.strategy &&
      other.sendOnlyPaymentConfirmation === this.sendOnlyPaymentConfirmation &&
      other.allowExtendedCartSize === this.allowExtendedCartSize &&
      other.deferOrderCompletion === this.deferOrderCompletion;
  }

  copyWith(opts?: {
    sendOnlyPaymentConfirmation?: boolean,
    allowExtendedCartSize?: boolean,
    deferOrderCompletion?: boolean,
  }): this {
    return new TicketsOnlyEventBookingProtocol(
        opts?.sendOnlyPaymentConfirmation ?? this.sendOnlyPaymentConfirmation,
        opts?.allowExtendedCartSize ?? this.allowExtendedCartSize,
        opts?.deferOrderCompletion ?? this.deferOrderCompletion,
    ) as this;
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof TicketsOnlyEventBookingProtocol;
  }

  static assertType(order: DataModel): order is TicketsOnlyEventBookingProtocol {
    return order instanceof TicketsOnlyEventBookingProtocol;
  }
}

export class TicketsOnlyEventBookingProtocolBuilder extends DataModelBuilder<TicketsOnlyEventBookingProtocol> {
  build(context: SerializationContext, map: SerializedData): TicketsOnlyEventBookingProtocol {
    return TicketsOnlyEventBookingProtocol.fromMap(context, map);
  }
}
